import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Typography,
  Tooltip,
  Autocomplete,
  TextField,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import AdminService, { Coach, Player } from '@services/adminService';

interface PlayerCoachLinkingProps {
  coach: Coach;
  onClose: () => void;
}

const PlayerCoachLinking: React.FC<PlayerCoachLinkingProps> = ({ coach, onClose }) => {
  const [linkedPlayers, setLinkedPlayers] = useState<Player[]>([]);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [playerToUnlink, setPlayerToUnlink] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [coach.id]);

  const loadData = async () => {
    try {
      const [linked, all] = await Promise.all([
        AdminService.getPlayersForCoach(coach.id),
        AdminService.getPlayers({ limit: 100 }),
      ]);
      setLinkedPlayers(linked.data || []);
      setAllPlayers(all.data || []);
    } catch (err) {
      setError('Failed to load players');
    }
  };

  const handleLinkPlayer = async () => {
    if (!selectedPlayer) return;

    setLoading(true);
    try {
      await AdminService.linkPlayerToCoach(selectedPlayer.id, coach.id);
      setSuccess('Player linked successfully!');
      setSelectedPlayer(null);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to link player');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlinkPlayer = (playerId: string) => {
    setPlayerToUnlink(playerId);
    setDeleteConfirmOpen(true);
  };

  const confirmUnlink = async () => {
    if (!playerToUnlink) return;

    setLoading(true);
    try {
      await AdminService.unlinkPlayerFromCoach(playerToUnlink, coach.id);
      setSuccess('Player unlinked successfully!');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to unlink player');
    } finally {
      setLoading(false);
      setDeleteConfirmOpen(false);
      setPlayerToUnlink(null);
    }
  };

  const availablePlayers = allPlayers.filter(
    (p) => !linkedPlayers.some((lp) => lp.id === p.id)
  );

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Manage Players for {coach.user.firstName} {coach.user.lastName} ({coach.coachIdCode})
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Add Player to Coach
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Autocomplete
              fullWidth
              options={availablePlayers}
              getOptionLabel={(option) => `${option.playerIdCode} - ${option.user.firstName} ${option.user.lastName} (${option.primarySport || 'No sport'})`}
              value={selectedPlayer}
              onChange={(event, newValue) => setSelectedPlayer(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Player"
                  placeholder="Type to search by name or ID..."
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">
                      <strong>{option.playerIdCode}</strong> - {option.user.firstName} {option.user.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.primarySport || 'No sport'} • {option.level} • {option.country || 'No country'}
                    </Typography>
                  </Box>
                </li>
              )}
            />
            <Button
              variant="contained"
              onClick={handleLinkPlayer}
              disabled={!selectedPlayer || loading}
              startIcon={<AddIcon />}
              sx={{ minWidth: '100px', height: '56px' }}
            >
              Link
            </Button>
          </Box>
        </Box>

        <Typography variant="subtitle1" gutterBottom>
          Linked Players ({linkedPlayers.length})
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Player ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Sport</TableCell>
                <TableCell>Level</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {linkedPlayers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No players linked to this coach yet
                  </TableCell>
                </TableRow>
              ) : (
                linkedPlayers.map((player) => (
                  <TableRow key={player.id}>
                    <TableCell>
                      <Chip label={player.playerIdCode} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{player.user.firstName} {player.user.lastName}</TableCell>
                    <TableCell>{player.user.email}</TableCell>
                    <TableCell>{player.primarySport || '-'}</TableCell>
                    <TableCell>
                      <Chip label={player.level} size="small" />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Unlink Player">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleUnlinkPlayer(player.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Unlink Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Unlink</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to unlink this player from {coach.user.firstName} {coach.user.lastName}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteConfirmOpen(false); setPlayerToUnlink(null); }}>
            Cancel
          </Button>
          <Button onClick={confirmUnlink} color="error" variant="contained" disabled={loading}>
            {loading ? 'Unlinking...' : 'Unlink'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default PlayerCoachLinking;
